import { useState, useEffect } from 'react';
import { RestaurantData } from '../services/airtableService';

export interface UseRestaurantSlugReturn {
  restaurantData: RestaurantData | null;
  isLoading: boolean;
  error: string | null;
  isValidSlug: boolean;
  slugChecked: boolean;
}

export const useRestaurantSlug = (slug: string | null): UseRestaurantSlugReturn => {
  const [restaurantData, setRestaurantData] = useState<RestaurantData | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [slugChecked, setSlugChecked] = useState(false);

  useEffect(() => {
    if (!slug) {
      setRestaurantData(null);
      setIsLoading(false);
      setError(null);
      setSlugChecked(true);
      return;
    }

    const checkSlug = async () => {
      setIsLoading(true);
      setError(null);
      setSlugChecked(false);

      try {
        // Check if we have the required environment variables
        if (!process.env.REACT_APP_AIRTABLE_API_KEY || !process.env.REACT_APP_AIRTABLE_BASE_ID) {
          console.warn('Airtable credentials not configured, skipping slug validation');
          setRestaurantData(null);
          setSlugChecked(true);
          setIsLoading(false);
          return;
        }

        // For demo purposes, simulate valid slugs without calling Airtable
        const knownSlugs = ['ashoka', 'spice-garden', 'ocean-view', 'bangeras-kitchen'];
        if (knownSlugs.includes(slug.toLowerCase())) {
          setRestaurantData({
            id: 'demo-' + slug,
            slug: slug,
            name: slug.replace(/[-_]/g, ' ').split(' ').map(word => 
              word.charAt(0).toUpperCase() + word.slice(1)
            ).join(' ') + ' Restaurant',
            voiceflowProjectId: '687775105bc1109b79a6075c',
          });
        } else {
          setRestaurantData(null);
        }
        setSlugChecked(true);
      } catch (err) {
        console.error('Error checking slug:', err);
        
        // For demo purposes, if Airtable fails, still allow common slugs
        const knownSlugs = ['ashoka', 'spice-garden', 'ocean-view', 'bangeras-kitchen'];
        if (knownSlugs.includes(slug.toLowerCase())) {
          console.warn('Airtable unavailable, using fallback data for known slug:', slug);
          setRestaurantData({
            id: 'fallback-' + slug,
            slug: slug,
            name: slug.replace(/[-_]/g, ' ').split(' ').map(word => 
              word.charAt(0).toUpperCase() + word.slice(1)
            ).join(' ') + ' Restaurant',
            voiceflowProjectId: '687775105bc1109b79a6075c',
          });
          setError(null);
        } else {
          setError('Restaurant not found. Please request a demo below.');
          setRestaurantData(null);
        }
        setSlugChecked(true);
      } finally {
        setIsLoading(false);
      }
    };

    checkSlug();
  }, [slug]);

  return {
    restaurantData,
    isLoading,
    error,
    isValidSlug: restaurantData !== null,
    slugChecked
  };
};